import config from "../../config.json";
import { getLudDocumentFileUrlAPI, getStoreLudDocumentsAPI, uploadWithProgress } from "./ludDocuments";

// Unified report analysis (lud-backend routes/reportRoutes.js):
//   GET  report/analyze-files/status   { limits: { maxFiles, maxFileBytes } }
//   POST report/validate-files         multipart: storeId, mode, year, month, files (JSON [{ app, section, kind, partKey }]), file (repeated)
//   POST report/analyze-files/preview  multipart: storeId, mode, year, month, manifest (JSON [{ file, app, section, kind, operation, partKey, expectedVersion }]),
//                                      one form field per file, confirmationToken?, acknowledgements? (JSON codes)
//   POST report/analyze-files          the same + planFingerprint?
// Files are sent as their original bytes. Failures reject with error.status and error.data
// (the backend's { code, message, saved, ... }; saved: false = nothing was written, null = not
// known); nothing here logs file contents or tokens.

const BASE_AUTH_API = String(config.BASE_AUTH_API || "").replace(/\/+$/, "");

export const getAnalysisStatusAPI = async (token) => {
  let response;
  try {
    response = await fetch(`${BASE_AUTH_API}/report/analyze-files/status`, { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } });
  } catch (error) {
    throw Object.assign(new Error("Failed to fetch"), { status: 0, data: { code: "NETWORK_ERROR" } });
  }
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.success) throw Object.assign(new Error(data?.message || "Request failed"), { status: response.status, data });
  return { limits: data.limits || {} };
};

const appendContext = (form, { storeId, mode, year, month }) => {
  form.append("storeId", String(storeId));
  form.append("mode", mode);
  form.append("year", String(year));
  form.append("month", String(month));
};

// files: [{ file (File/Blob), name, app, section, kind, operation, partKey?, expectedVersion? }]
export const validateReportFilesAPI = (token, context, files, { onProgress, signal, confirmationToken } = {}) => {
  const form = new FormData();
  appendContext(form, context);
  form.append("files", JSON.stringify(files.map(({ app, section, kind, operation, partKey }) => ({ app, section, kind, ...(operation === "replace" ? { partKey } : {}) }))));
  if (confirmationToken) form.append("confirmationToken", confirmationToken);
  files.forEach((item) => form.append("file", item.file, item.name));
  return uploadWithProgress({ token, endpoint: "report/validate-files", method: "POST", formData: form, onProgress, signal }).then((data) => data.validation);
};

const ingestionForm = (context, files, { confirmationToken, acknowledgements, planFingerprint } = {}) => {
  const form = new FormData();
  appendContext(form, context);
  form.append("manifest", JSON.stringify(files.map(({ app, section, kind, operation, partKey, expectedVersion }, index) => ({
    file: `file${index}`, app, section, kind, operation,
    ...(operation === "replace" ? { partKey, expectedVersion } : {}),
  }))));
  if (confirmationToken) form.append("confirmationToken", confirmationToken);
  if (acknowledgements?.length) form.append("acknowledgements", JSON.stringify(acknowledgements));
  if (planFingerprint) form.append("planFingerprint", planFingerprint);
  files.forEach((item, index) => form.append(`file${index}`, item.file, item.name));
  return form;
};

export const previewAnalysisAPI = (token, context, files, { confirmationToken, acknowledgements, onProgress, signal } = {}) =>
  uploadWithProgress({ token, endpoint: "report/analyze-files/preview", method: "POST", formData: ingestionForm(context, files, { confirmationToken, acknowledgements }), onProgress, signal });

export const commitAnalysisAPI = (token, context, files, { confirmationToken, acknowledgements, planFingerprint, onProgress, signal } = {}) =>
  uploadWithProgress({ token, endpoint: "report/analyze-files", method: "POST", formData: ingestionForm(context, files, { confirmationToken, acknowledgements, planFingerprint }), onProgress, signal });

// Report-file parts of a store (lud-documents record; same endpoint as the documentation page).
export const getStoreReportPartsAPI = async (token, storeCode) => {
  const record = await getStoreLudDocumentsAPI(token, storeCode);
  return Array.isArray(record?.reportFiles) ? record.reportFiles : [];
};

// The stored bytes of an existing part, through the authorized signed-download link.
export const downloadReportPartAPI = async (token, storeCode, part) => {
  const link = await getLudDocumentFileUrlAPI(token, storeCode, { kind: "report", reference: part._id || part.id });
  if (!link?.url) throw Object.assign(new Error("The file link is not available"), { status: 0, data: { code: "DOWNLOAD_FAILED" } });
  const response = await fetch(link.url);
  if (!response.ok) throw Object.assign(new Error(`Download failed (${response.status})`), { status: response.status, data: { code: "DOWNLOAD_FAILED" } });
  const blob = await response.blob();
  const name = part.currentFile?.uploadedName || link.originalName || part.currentFile?.originalName || "report-file";
  return { blob, name };
};
